import React, {useState} from 'react';
import createAxiosInstance from '../support/AxiosSupport';
import {MapPinIcon} from './icons';

/** Причины недоступности погоды, которые лечатся вводом локации/согласия в UI (CTHS-29 AC2). */
export const FIXABLE_WEATHER_REASONS = new Set(['DISABLED', 'NO_LOCATION', 'NO_CONSENT']);

interface Props {
    /** Префикс data-testid (`home-outfit`, `outfit-pick`) — форма используется на нескольких экранах. */
    testIdPrefix: string;
    /** Заголовок плашки; по умолчанию — приглашение включить погоду. */
    title?: string;
    /** Вызывается после успешного сохранения локации — родитель перезагружает данные. */
    onSaved: () => void | Promise<void>;
}

/**
 * Плашка «Включить погоду» с формой локации и согласия (PUT /weather/location, CTHS-27).
 * Вынесена из HomeOutfitCard, чтобы подбор образа дня показывал тот же CTA при
 * «Погода не учтена» (QA-2), а не дублировал форму. Только семантические токены и
 * dark:-варианты, тач-зоны ≥44px — по styleguide.
 */
const WeatherLocationForm: React.FC<Props> = ({testIdPrefix, title, onSaved}) => {
    const [showForm, setShowForm] = useState(false);
    const [city, setCity] = useState('');
    const [lat, setLat] = useState('');
    const [lng, setLng] = useState('');
    const [consent, setConsent] = useState(false);
    const [geoBusy, setGeoBusy] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const tid = (suffix: string) => `${testIdPrefix}-${suffix}`;

    const detectLocation = () => {
        setError(null);
        if (typeof navigator === 'undefined' || !navigator.geolocation) {
            setError('Геолокация недоступна — введите координаты вручную');
            return;
        }
        setGeoBusy(true);
        navigator.geolocation.getCurrentPosition(
            pos => {
                setLat(pos.coords.latitude.toFixed(4));
                setLng(pos.coords.longitude.toFixed(4));
                setGeoBusy(false);
            },
            () => {
                setError('Не удалось определить местоположение — введите координаты вручную');
                setGeoBusy(false);
            },
        );
    };

    const latNum = Number(lat);
    const lngNum = Number(lng);
    const coordsValid = lat.trim() !== '' && lng.trim() !== ''
        && !Number.isNaN(latNum) && !Number.isNaN(lngNum)
        && latNum >= -90 && latNum <= 90 && lngNum >= -180 && lngNum <= 180;

    const save = async () => {
        if (!coordsValid || !consent || saving) return;
        setSaving(true);
        setError(null);
        try {
            const api = await createAxiosInstance();
            await api.put('/weather/location', {
                city: city.trim() || undefined,
                latitude: latNum,
                longitude: lngNum,
                consentGranted: true,
            });
            setShowForm(false);
            await onSaved();
        } catch (e) {
            setError('Не удалось сохранить локацию. Попробуйте ещё раз.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="rounded-xl border border-dashed border-gray-300 dark:border-ink-700 p-4 space-y-3"
            data-testid={tid('weather-cta')}
        >
            <div className="flex items-start gap-3">
                <MapPinIcon className="w-6 h-6 text-brand-600 dark:text-brand-400 shrink-0 mt-0.5"/>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                    {title ?? 'Включите погоду, чтобы подбирать образ под прогноз.'} Локация хранится огрублённо и
                    только с вашего согласия.
                </p>
            </div>
            {!showForm ? (
                <button
                    type="button"
                    onClick={() => setShowForm(true)}
                    data-testid={tid('enable-weather')}
                    className="min-h-[44px] px-5 rounded-xl bg-brand-600 text-white hover:bg-brand-700"
                >
                    Включить погоду
                </button>
            ) : (
                <div className="space-y-3" data-testid={tid('location-form')}>
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={detectLocation}
                            disabled={geoBusy}
                            data-testid={tid('detect-location')}
                            className="min-h-[44px] px-4 rounded-xl border border-gray-300 dark:border-ink-700 text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-ink-700 disabled:opacity-50 inline-flex items-center gap-2"
                        >
                            <MapPinIcon className="w-4 h-4"/>
                            {geoBusy ? 'Определяем…' : 'Определить автоматически'}
                        </button>
                    </div>
                    <input
                        value={city}
                        onChange={e => setCity(e.target.value)}
                        placeholder="Город (необязательно)"
                        data-testid={tid('city')}
                        className="w-full min-h-[44px] px-3 rounded-xl border border-gray-300 dark:border-ink-700 bg-white dark:bg-ink-800 text-gray-900 dark:text-gray-100"
                    />
                    <div className="flex gap-2">
                        <input
                            value={lat}
                            onChange={e => setLat(e.target.value)}
                            inputMode="decimal"
                            placeholder="Широта"
                            data-testid={tid('lat')}
                            className="w-full min-h-[44px] px-3 rounded-xl border border-gray-300 dark:border-ink-700 bg-white dark:bg-ink-800 text-gray-900 dark:text-gray-100"
                        />
                        <input
                            value={lng}
                            onChange={e => setLng(e.target.value)}
                            inputMode="decimal"
                            placeholder="Долгота"
                            data-testid={tid('lng')}
                            className="w-full min-h-[44px] px-3 rounded-xl border border-gray-300 dark:border-ink-700 bg-white dark:bg-ink-800 text-gray-900 dark:text-gray-100"
                        />
                    </div>
                    <label className="flex items-start gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={consent}
                            onChange={e => setConsent(e.target.checked)}
                            data-testid={tid('consent')}
                            className="mt-1 accent-brand-600 w-5 h-5 shrink-0"
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                            Согласен на хранение и использование моей локации для прогноза погоды.
                        </span>
                    </label>
                    {error && (
                        <p className="text-rose-600 dark:text-rose-400 text-sm" data-testid={tid('location-error')}>
                            {error}
                        </p>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={save}
                            disabled={!coordsValid || !consent || saving}
                            data-testid={tid('save-location')}
                            className="min-h-[44px] px-5 rounded-xl bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
                        >
                            {saving ? 'Сохранение…' : 'Сохранить'}
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setShowForm(false);
                                setError(null);
                            }}
                            className="min-h-[44px] px-4 rounded-xl border border-gray-300 dark:border-ink-700 text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-ink-700"
                        >
                            Отмена
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WeatherLocationForm;
